import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, RowClickedEvent } from 'ag-grid-community';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { HlmBtnDirective, HlmIconComponent, HlmInputDirective, HlmSpinnerComponent, ToastService } from '../../../../ui';
import { PermissionService } from '../../../../core/auth/permission.service';
import { BrandService } from '../../brand.service';
import { Brand } from '../../brand.model';
import { DomainService } from '../domain.service';
import { BrandDomain, Domain, SubBrand } from '../domain.model';
import { SubBrandService } from '../../sub-brands/sub-brand.service';
import { BrandDomainService } from '../brand-domain.service';
import { ConfirmDialogComponent } from '../../../../shared/confirm-dialog/confirm-dialog.component';
import { BrandReadonlyBannerComponent } from '../../brand-readonly-banner.component';
import { DomainFormDialogComponent, DomainFormDialogData } from './domain-form-dialog.component';
import { DomainActionCellComponent, DomainGridContext } from './domain-action-cell.component';

export interface DomainRow {
  domain: string;
  mx: string | null;
  defaultBrand: number | null;
  defaultSubBrand: number | null;
  defaultBrandName: string;
  defaultSubBrandName: string;
  availableForBrandIds: number[];
  availableForNames: string;
}

@Component({
  selector: 'cmt-domain-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, AgGridAngular, HlmBtnDirective, HlmIconComponent, HlmInputDirective, HlmSpinnerComponent, BrandReadonlyBannerComponent],
  templateUrl: './domain-list.component.html',
  styleUrl: './domain-list.component.scss',
})
export class DomainListComponent {
  private readonly domainService = inject(DomainService);
  private readonly subBrandService = inject(SubBrandService);
  private readonly brandService = inject(BrandService);
  private readonly brandDomainService = inject(BrandDomainService);
  private readonly permissionService = inject(PermissionService);
  private readonly dialog = inject(MatDialog);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly canWrite = computed(() => this.permissionService.canWrite('brandManager'));
  protected readonly loading = signal(true);
  protected readonly filterText = signal('');
  private readonly domains = signal<Domain[]>([]);
  private readonly brandsById = signal<Map<number, string>>(new Map());
  private readonly subBrandsById = signal<Map<number, string>>(new Map());
  /** Reverse map: domain string → list of brand IDs that have it in MANDANT_DOMAIN. */
  private readonly domainToBrandIds = signal<Map<string, number[]>>(new Map());

  /** Display rows — brand IDs resolved to names. Derived, never set in a subscribe (Pitfall #13). */
  readonly rows = computed<DomainRow[]>(() => {
    const names = this.brandsById();
    const subNames = this.subBrandsById();
    const reverseMap = this.domainToBrandIds();
    const q = this.filterText().trim().toLowerCase();
    return this.domains()
      .map(d => {
        const brandIds = reverseMap.get(d.domain) ?? [];
        const availableForNames = brandIds.length > 0
          ? brandIds.map(id => names.get(id) ?? `#${id}`).join(', ')
          : '—';
        return {
          domain: d.domain,
          mx: d.mx,
          defaultBrand: d.defaultBrand,
          defaultSubBrand: d.defaultSubBrand,
          defaultBrandName: d.defaultBrand != null ? (names.get(d.defaultBrand) ?? `#${d.defaultBrand}`) : '—',
          defaultSubBrandName: d.defaultSubBrand != null ? (subNames.get(d.defaultSubBrand) ?? `#${d.defaultSubBrand}`) : '—',
          availableForBrandIds: brandIds,
          availableForNames,
        };
      })
      .filter(r => !q || r.domain.toLowerCase().includes(q) || r.defaultBrandName.toLowerCase().includes(q));
  });

  protected readonly columnDefs: ColDef<DomainRow>[] = [
    { field: 'domain', headerName: 'Domain', minWidth: 200, flex: 1.4, cellClass: 'cmt-mono' },
    { field: 'mx', headerName: 'MX', minWidth: 150, flex: 1, cellClass: 'cmt-mono',
      valueFormatter: (p: { value: string | null }) => p.value ?? '—' },
    { field: 'defaultBrandName', headerName: 'Default Brand', minWidth: 160, flex: 1 },
    { field: 'defaultSubBrandName', headerName: 'Default Sub-Brand', minWidth: 160, flex: 1 },
    { field: 'availableForNames', headerName: 'Available for', minWidth: 200, flex: 1.4,
      tooltipField: 'availableForNames' },
    {
      colId: 'domain-actions',
      headerName: 'Actions',
      minWidth: 110,
      maxWidth: 130,
      sortable: false,
      filter: false,
      resizable: false,
      cellRenderer: DomainActionCellComponent,
    },
  ];
  protected readonly defaultColDef: ColDef = { sortable: true, resizable: true, filter: 'agTextColumnFilter' };

  /** AG-Grid context wired to the grid — action-cell uses this to call back into the component. */
  protected readonly gridContext: DomainGridContext = {
    canWrite: () => this.canWrite(),
    confirmDelete: (row: DomainRow) => this.confirmDelete(row),
  };

  constructor() { this.reload(); }

  protected reload(): void {
    this.loading.set(true);
    // Single end-to-end pipeline: the inner per-brand membership fetch is chained
    // via switchMap so a new reload() cancels the previous one in-flight, and
    // takeUntilDestroyed tears everything down on destroy (no nested-subscribe leak).
    forkJoin({
      domains: this.domainService.listDomains(),
      brands: this.brandService.listBrands(true),
      subBrands: this.subBrandService.listSubBrands(),
    }).pipe(
      switchMap(({ domains, brands, subBrands }) => {
        this.brandsById.set(new Map(brands.map((b: Brand) => [b.id, b.name])));
        this.subBrandsById.set(new Map(subBrands.map((s: SubBrand) => [s.id, s.name])));
        this.domains.set(domains);

        if (brands.length === 0) {
          return of([] as { brandId: number; domains: BrandDomain[] }[]);
        }
        // Fetch per-brand domain memberships in parallel. Non-fatal: a failure
        // here shows the grid without the "Available for" data rather than
        // blocking the whole page (preserves the prior inner-error behavior).
        return forkJoin(
          brands.map((b: Brand) =>
            this.brandDomainService.listBrandDomains(b.id).pipe(
              map(ds => ({ brandId: b.id, domains: ds })),
            ),
          ),
        ).pipe(
          catchError(() => {
            this.toastService.open('Could not load brand-domain memberships', 'OK', { duration: 5000 });
            return of([] as { brandId: number; domains: BrandDomain[] }[]);
          }),
        );
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: results => {
        const reverseMap = new Map<string, number[]>();
        for (const { brandId, domains: brandDomains } of results) {
          for (const d of brandDomains) {
            const list = reverseMap.get(d.domain);
            if (list) {
              list.push(brandId);
            } else {
              reverseMap.set(d.domain, [brandId]);
            }
          }
        }
        this.domainToBrandIds.set(reverseMap);
        this.loading.set(false);
      },
      error: () => { this.loading.set(false); this.toastService.open('Failed to load domains', 'OK', { duration: 5000 }); },
    });
  }

  protected onGridReady(_e: GridReadyEvent<DomainRow>): void {
    // reserved — grid API stored here if needed in future (e.g. programmatic filter/sort)
  }
  protected onFirstDataRendered(e: FirstDataRenderedEvent): void { e.api.sizeColumnsToFit(); }
  protected onGridSizeChanged(e: GridSizeChangedEvent): void { e.api.sizeColumnsToFit(); }

  /** Row click opens the edit dialog — same affordance as the Brands list. The
   *  Delete button stops propagation, so it never triggers this. */
  protected onRowClicked(event: RowClickedEvent<DomainRow>): void {
    if (event.data) this.openEdit(event.data);
  }

  protected openAdd(): void { this.openForm(null); }
  protected openEdit(row: DomainRow): void {
    this.openForm({ domain: row.domain, mx: row.mx, defaultBrand: row.defaultBrand, defaultSubBrand: row.defaultSubBrand });
  }

  private openForm(existing: Domain | null): void {
    if (!this.canWrite()) return;
    const brands = [...this.brandsById()].map(([id, name]) => ({ id, name }));
    const data: DomainFormDialogData = {
      existing,
      brands,
      linkedBrandIds: existing ? (this.domainToBrandIds().get(existing.domain) ?? []) : [],
    };
    this.dialog.open(DomainFormDialogComponent, { data, width: '560px', maxWidth: '92vw', autoFocus: false, panelClass: 'cmt-dialog-panel' })
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(saved => { if (saved) this.reload(); });
  }

  protected confirmDelete(row: DomainRow): void {
    if (!this.canWrite()) return;
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete domain?',
        message: `${row.domain} will be removed from the global DOMAINS table. This fails if any brand still maps this domain — remove those brand mappings first.`,
        confirmLabel: 'Delete', cancelLabel: 'Cancel', confirmColor: 'warn', icon: 'warning',
      },
      width: '460px', maxWidth: '92vw', autoFocus: false, panelClass: 'cmt-dialog-panel',
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(ok => { if (ok) this.delete(row.domain); });
  }

  private delete(domain: string): void {
    this.domainService.deleteDomain(domain)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: () => { this.toastService.open('Domain deleted', 'OK', { duration: 3000 }); this.reload(); },
      error: err => {
        // Backend returns 500 for the FK "still referenced by MANDANT_DOMAIN" case
        // (see contract-service DomainController @ApiResponse 500); accept 409 too in
        // case the API is later corrected to a Conflict.
        const msg = (err?.status === 500 || err?.status === 409)
          ? 'Domain is still referenced by a brand — remove the brand mappings first.'
          : 'Delete failed — please retry.';
        this.toastService.open(msg, 'OK', { duration: 5000 });
      },
    });
  }
}
